import { FC, useMemo, useState } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../store/hooks";
import {
  alertService,
  getDocument,
  getDocumentShipmentsForPayment,
  loaderService,
} from "../../services";
import DocumentDetailsModal from "./DocumentDetailsModal";
import {
  DocumentInterface,
  DocumentType,
  PaymentMode,
  ShipmentInterface,
  StoreDocumentInterface,
} from "../../interfaces";
import {
  creditNoteStatusFormat,
  currencyExchangeText,
  documentStatusFormat,
  useCurrencyExchanges,
} from "../../utils";
import {
  ShipmentCreatePhase,
  setShipmentCreatePhase,
  setShipmentCreateDocument,
  setShipmentCreateList,
} from "../../store/slices";
import { BUCreditDTO } from "../../interfaces/Dtos/BUCreditDTO";

export enum DocumentTableField {
  ID,
  DATE,
  BILLING_DAYS,
  OWNER,
  AMOUNT,
  TO_PAID,
  EXCESS,
  DETAILS,
  STATUS,
  TYPE,
}
interface DocumentTableItemProps {
  index: number;
  document: StoreDocumentInterface;
  fields: DocumentTableField[];
  buCredit?: BUCreditDTO;
}
export const DocumentTableItem: FC<DocumentTableItemProps> = ({
  index,
  fields,
  document,
  buCredit,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const exchanges = useCurrencyExchanges();
  const [openModal, setOpenModal] = useState(false);
  const [fullDocument, setFullDocument] = useState<DocumentInterface>();

  const daysSinceCreation = useMemo(() => {
    if (!document.documentDate) return 0;

    const documentDate = new Date(document.documentDate);
    const currentDate = new Date();
    const diffTime = Math.abs(currentDate.getTime() - documentDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }, [document.documentDate]);

  const defeated = useMemo(() => {
    return (
      !!buCredit &&
      (document?.paymentModeID === PaymentMode.BOXOFFICE_CREDIT ||
        document?.paymentModeID === PaymentMode.DEST_BOXOFFICE_CREDIT) &&
      buCredit.creditDays < daysSinceCreation
    );
  }, [buCredit, daysSinceCreation, document?.paymentModeID]);

  const getFullDocument = async () => {
    const updatedDocument = await getDocument(document.documentID);
    if (!updatedDocument) {
      alertService.error("No se pudo obtener el documento");
      return;
    }

    // No hydration on open. The table paints the light rows the document
    // already carries: since the API sends the store zone and the structured
    // address, they compose the same text the full guía composed, and the N
    // requests per invoice that this screen was known for are gone.
    setFullDocument(updatedDocument);
  };

  const onPay = async () => {
    if (!fullDocument) return;

    // Read here instead, because paying needs what the light row does not
    // carry: the items, which are what the subtotal and the VAT are computed
    // over. Moving the cost from open to pay does not raise the worst case, a
    // cashier who opens and pays spends the same, and everyone who only looks
    // stops paying anything. Bringing it down to one request is delivery 3.
    loaderService.start();
    const document = await getDocumentShipmentsForPayment(fullDocument);
    loaderService.stop();

    if (!document) {
      alertService.error(
        "No se pudieron obtener todas las guías de la factura",
        "Vuelva a intentarlo en unos segundos."
      );
      return;
    }

    setFullDocument(document);
    dispatch(setShipmentCreateDocument(document));
    // Explicit now that the reducer no longer derives the list from the
    // document: only a caller holding full guías may fill it.
    dispatch(setShipmentCreateList(document.shipments as ShipmentInterface[]));
    dispatch(setShipmentCreatePhase(ShipmentCreatePhase.PAYMENT));
    navigate("/shipment-create");
  };

  return (
    <tr
      key={index}
      className={classNames(
        "cursor-pointer hover:bg-gray-100",
        index % 2 === 0 && "bg-gray-50"
      )}
      onClick={() => {
        getFullDocument();
        setOpenModal(true);
      }}
    >
      {fields.includes(DocumentTableField.ID) && (
        <td
          className="px-4 py-4 text-xs text-gray-500 font-semibold truncate"
          style={{ maxWidth: "10rem" }}
        >
          {document.documentNumber ?? document.documentID.slice(0, 8)}

          <div onClick={(e) => e.stopPropagation()}>
            <DocumentDetailsModal
              openModal={openModal}
              document={fullDocument}
              buCredit={buCredit}
              onPay={onPay}
              setOpenModal={setOpenModal}
              setSelectedDocument={setFullDocument}
            />
          </div>
        </td>
      )}

      {fields.includes(DocumentTableField.DATE) && (
        <td className="px-4 py-4 text-xs text-gray-500 font-semibold truncate">
          <p>
            {!!document.creationDate &&
              new Date(document.creationDate)
                .toLocaleDateString("es-VE", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
                .replace("a", "AM")
                .replace("p", "PM")
                .slice(0, -4)}
          </p>

          {fields.includes(DocumentTableField.BILLING_DAYS) && (
            <p className="text-gray-800 font-normal">
              Días desde Facturación:{" "}
              <span className="text-gray-900 font-semibold">
                {daysSinceCreation}
              </span>
            </p>
          )}
        </td>
      )}

      {fields.includes(DocumentTableField.OWNER) && (
        <td className="px-4 py-4 text-xs text-gray-500 font-semibold">
          {document.accountName}
        </td>
      )}

      {fields.includes(DocumentTableField.AMOUNT) && (
        <td className="px-4 py-4 text-xs text-gray-500 font-semibold truncate">
          {currencyExchangeText(document.totalAmountBaseCurr, exchanges, "USD")}
        </td>
      )}

      {fields.includes(DocumentTableField.TO_PAID) && (
        <td className="px-4 py-4 text-xs text-gray-500 font-semibold truncate">
          <p
            className={classNames(
              document.documentTypeID === DocumentType.CREDIT_NOTE && "hidden"
            )}
          >
            {currencyExchangeText(
              Math.max(0, document.balanceAmountBaseCurr),
              exchanges,
              "USD"
            )}
          </p>
        </td>
      )}

      {fields.includes(DocumentTableField.EXCESS) && (
        <td className="px-4 py-4 text-xs text-gray-500 font-semibold truncate">
          {currencyExchangeText(
            Math.max(0, -document.balanceAmountBaseCurr),
            exchanges,
            "USD"
          )}
        </td>
      )}

      {fields.includes(DocumentTableField.DETAILS) && (
        <td className="px-4 py-4 text-xs text-gray-500 font-semibold truncate">
          <p>
            {document.documentTypeID === DocumentType.CREDIT_NOTE &&
              !!document.documentAppliedNumber &&
              `Factura original: ${document.documentAppliedNumber}`}
          </p>
          <p>
            {document.documentTypeID === DocumentType.CREDIT_NOTE &&
              !!document.documentAffectedNumber &&
              `Factura nueva: ${document.documentAffectedNumber}`}
          </p>
          <p>
            {document.documentTypeID !== DocumentType.CREDIT_NOTE &&
              `Cant. de guías: ${document.countShipmentDocument}`}
          </p>
        </td>
      )}

      {fields.includes(DocumentTableField.TYPE) && (
        <td className="px-4 py-4 text-xs text-gray-500 font-semibold truncate">
          {document.documentTypeID === DocumentType.CREDIT_NOTE
            ? "Nota de Crédito"
            : document.documentTypeID === DocumentType.INVOICE
            ? "Factura"
            : "Orden"}
        </td>
      )}

      {fields.includes(DocumentTableField.STATUS) && (
        <td className="px-4 py-4 text-xs text-right text-gray-500 font-semibold truncate">
          {defeated
            ? "Vencido"
            : document.documentTypeID === DocumentType.CREDIT_NOTE
            ? creditNoteStatusFormat(document.statusID)
            : documentStatusFormat(document.statusID)}
        </td>
      )}
    </tr>
  );
};
